import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import { getComplaintsList, getComplaintsStats } from "../../services/complaint.service.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import FilterComplaints from "../../components/complaints/FilterComplaints.tsx";
import PaginationComplaints from "../../components/complaints/PaginationComplaints.tsx";
import {
  setRecords,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
  setShowDeleteModal,
  setCurrentRecord,
} from "../../store/slices/complaintsSlice.ts";
import { Tooltip } from '@mui/material';
import EmptyImage from "../../assets/img/ic_content.svg";
import ModalContainer from "../../components/ModalContainer.tsx";
import DeleteComplaintModal from "../../components/complaints/DeleteComplaintModal.tsx";
moment.locale("es");

interface ComplaintParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  branchId: string;
  positionId: string;
}

interface ComplaintStats {
  total: number;
  pendientes: number;
  revisadas: number;
  anonimas: number;
}

const StatCard = ({
  icon, label, value, color, bg, border,
}: {
  icon: string; label: string; value: number;
  color: string; bg: string; border: string;
}) => (
  <div style={{
    flex: "1 1 160px", background: bg, border: `1px solid ${border}`,
    borderRadius: 14, padding: "1rem 1.25rem",
    display: "flex", alignItems: "center", gap: "0.85rem", minWidth: 0,
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: color, display: "flex", alignItems: "center",
      justifyContent: "center", flexShrink: 0,
    }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize: 16, color: "#fff" }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 500, marginTop: 3, whiteSpace: "nowrap" }}>{label}</div>
    </div>
  </div>
);

const AllComplaints = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ComplaintStats>({ total: 0, pendientes: 0, revisadas: 0, anonimas: 0 });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const {
    records,
    total,
    page,
    limit,
    query,
    sortBy,
    defaultSort,
    showDeleteModal,
    currentRecord,
  } = useSelector((state: RootState) => state.complaints);

  const complaintParams = useMemo((): ComplaintParams => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const branchId = query.includes("branchId=")
      ? query.split("branchId=")[1]?.split("&")[0] || ""
      : "";
    const positionId = query.includes("positionId=")
      ? query.split("positionId=")[1]?.split("&")[0] || ""
      : "";

    return {
      limit,
      offset,
      param,
      sort: sortBy,
      order: defaultSort.toUpperCase(),
      branchId,
      positionId,
    };
  }, [page, limit, query, sortBy, defaultSort]);

  const fetchComplaints = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getComplaintsList({
        limit: complaintParams.limit,
        offset: complaintParams.offset,
        order: complaintParams.order as "ASC" | "DESC",
        sort: complaintParams.sort,
        param: complaintParams.param || undefined,
        branchId: complaintParams.branchId
          ? Number(complaintParams.branchId)
          : undefined,
        positionId: complaintParams.positionId
          ? Number(complaintParams.positionId)
          : undefined,
      });
      dispatch(setRecords(response.records || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar quejas";
      setError(errorMessage);
      console.error("Error fetching complaints:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, complaintParams]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await getComplaintsStats();
      setStats(data);
    } catch {}
  }, []);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder =
        sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";

      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const formatDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("ddd DD MMM YYYY");
  };

  const thStyle: React.CSSProperties = {
    padding: "0.75rem 1rem",
    fontSize: "11px", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.06em",
    color: "#6b7280", background: "#f8fafc",
    borderBottom: "2px solid #e5e7eb",
    cursor: "pointer", whiteSpace: "nowrap",
  };

  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    const isRevisado = status === "revisado";
    return {
      backgroundColor: isRevisado ? "#dcfce7" : "#fef3c7",
      color: isRevisado ? "#15803d" : "#92400e",
      border: `1px solid ${isRevisado ? "#bbf7d0" : "#fde68a"}`,
      padding: "0.25rem 0.6rem",
      borderRadius: "999px",
      fontSize: "11px",
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      whiteSpace: "nowrap",
    };
  };

  const handleDeleteSuccess = () => {
    dispatch(setShowDeleteModal(false));
    dispatch(setCurrentRecord(null));
    fetchComplaints();
  };

  return (
    <Layout>
      <Title
        baseTitle="Quejas"
        basePath="/quejas"
        title="Listado de quejas"
      />

      <div className="card border-0 m-4 rounded shadow-sm">
        <div className="tab-pane fade show active" id="quejas">
          <div className="card-body">
            <Panel>
              {/* Stats */}
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                <StatCard icon="fa-list" label="Total quejas" value={stats.total} color="#6366f1" bg="#eef2ff" border="#c7d2fe" />
                <StatCard icon="fa-clock" label="Pendientes" value={stats.pendientes} color="#d97706" bg="#fffbeb" border="#fde68a" />
                <StatCard icon="fa-check-circle" label="Revisadas" value={stats.revisadas} color="#15803d" bg="#f0fdf4" border="#bbf7d0" />
                <StatCard icon="fa-user-secret" label="Anónimas" value={stats.anonimas} color="#6b7280" bg="#f9fafb" border="#e5e7eb" />
              </div>

              <FilterComplaints />

              <div
                className="table-responsive mb-3"
                style={{
                  height: "500px", overflowY: "auto", overflowX: "auto",
                  borderRadius: "12px", border: "1px solid #e5e7eb",
                }}
              >
                <table
                  className="table table-panel text-nowrap align-middle mb-0"
                  style={{ borderCollapse: "separate", borderSpacing: 0 }}
                >
                  <thead>
                    <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <th style={thStyle} onClick={() => handleSort("id")}>
                        Id {sortBy === "id" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("name")}>
                        Queja {sortBy === "name" && getIconSort()}
                      </th>
                      <th style={thStyle}>
                        Creado por
                      </th>
                      <th style={thStyle} onClick={() => handleSort("branch")}>
                        Sucursal {sortBy === "branch" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("position")}>
                        Puesto {sortBy === "position" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("createdAt")}>
                        Fecha {sortBy === "createdAt" && getIconSort()}
                      </th>
                      <th style={thStyle} onClick={() => handleSort("status")}>
                        Estatus {sortBy === "status" && getIconSort()}
                      </th>
                      <th style={{ ...thStyle, textAlign: "right", cursor: "default" }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records?.length > 0 ? (
                      records.map((complaint: any, idx: number) => {
                        const tdStyle: React.CSSProperties = {
                          padding: "0.65rem 1rem",
                          fontSize: "13px", color: "#374151",
                          borderBottom: "1px solid #f3f4f6",
                          verticalAlign: "middle",
                        };
                        const partnerName = complaint.partner?.person
                          ? toCamelCase(`${complaint.partner.person.firstName || ""} ${complaint.partner.person.lastName || ""} ${complaint.partner.person.secondLastName || ""}`.trim())
                          : null;

                        return (
                          <tr
                            key={complaint.id}
                            style={{ background: idx % 2 === 0 ? "#ffffff" : "#fafafa", transition: "background 0.1s" }}
                            onMouseEnter={e => (e.currentTarget.style.background = "#f0f9ff")}
                            onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#fafafa")}
                          >
                            <td style={{ ...tdStyle, fontWeight: 600, color: "#6b7280", fontSize: 12 }}>
                              #{complaint.id}
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 500 }}>
                              {toCamelCase(complaint.name)}
                            </td>
                            <td style={tdStyle}>
                              {complaint.anonymous ? (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                  background: "#f3f4f6", color: "#6b7280",
                                  padding: "0.2rem 0.55rem", borderRadius: "999px",
                                  fontSize: "11px", fontWeight: 500,
                                }}>
                                  <i className="fa-solid fa-user-secret" style={{ fontSize: 10 }} />
                                  Anónimo
                                </span>
                              ) : partnerName ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                  <div style={{
                                    width: 26, height: 26, borderRadius: "50%",
                                    background: "#eff6ff", border: "1px solid #bfdbfe",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0,
                                  }}>
                                    <i className="fa-solid fa-user" style={{ fontSize: 10, color: "#3b82f6" }} />
                                  </div>
                                  <span style={{ fontWeight: 500 }}>{partnerName}</span>
                                </div>
                              ) : (
                                <span style={{ color: "#9ca3af" }}>—</span>
                              )}
                            </td>
                            <td style={tdStyle}>{complaint.branch?.name || "-"}</td>
                            <td style={{ ...tdStyle, color: "#6b7280" }}>
                              {complaint.anonymous ? "—" : (complaint.position?.name || "-")}
                            </td>
                            <td style={{ ...tdStyle, color: "#6b7280", fontSize: 12 }}>
                              {formatDate(complaint.createdAt)}
                            </td>
                            <td style={tdStyle}>
                              <span style={getStatusBadgeStyle(complaint.status)}>
                                <i className={`fa-solid ${complaint.status === "revisado" ? "fa-check" : "fa-clock"}`} style={{ fontSize: 9 }} />
                                {complaint.status === "revisado" ? "Revisado" : "Pendiente"}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                              <Tooltip title="Ver detalles" arrow>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/quejas/editar/${complaint.id}`)}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                    padding: "0.3rem 0.7rem", borderRadius: "8px",
                                    fontSize: "12px", fontWeight: 600, cursor: "pointer",
                                    background: "#eff6ff", color: "#1d4ed8",
                                    border: "1px solid #bfdbfe",
                                  }}
                                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#dbeafe"; }}
                                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "#eff6ff"; }}
                                >
                                  <i className="fa fa-eye" style={{ fontSize: 11 }} /> Ver
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
                            <img src={EmptyImage} alt="" style={{ width: 140, opacity: 0.6, marginBottom: "1rem" }} />
                            <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 0.25rem" }}>
                              {error ? "Error al cargar quejas" : "Sin quejas"}
                            </p>
                            <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
                              {error ? error : "No se encontraron registros con los filtros aplicados"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <PaginationComplaints />
            </Panel>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />

      <ModalContainer
        open={showDeleteModal}
        closeFn={() => {
          dispatch(setShowDeleteModal(false));
          dispatch(setCurrentRecord(null));
        }}
        title={`Eliminar queja`}
        children={
          currentRecord && (
            <DeleteComplaintModal
              complaint={currentRecord}
              onSuccess={handleDeleteSuccess}
              closeFn={() => {
                dispatch(setShowDeleteModal(false));
                dispatch(setCurrentRecord(null));
              }}
            />
          )
        }
      />
    </Layout>
  );
};

export default AllComplaints;

